import { Box, Stack, CircularProgress, Alert } from "@mui/material";
import { ComgateOnboardingStatus, OrbitLPhase } from "Infrastructure/Api/Api";
import { useGetOrbitLStatusQuery } from "Api/Queries/Merchants/useGetOrbitLStatusQuery";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { Resources, useResource } from "Translations/Resources";
import { OrbitLChecklist } from "./OrbitLChecklist";
import { OrbitLContractConfirmation } from "./OrbitLContractConfirmation";
import { OrbitLRejectionPanel } from "./OrbitLRejectionPanel";
import { OrbitLStatusDisplay } from "./OrbitLStatusDisplay";
import { OrbitLHandoverConfirmation } from "./OrbitLHandoverConfirmation";
import type { ReactNode } from "react";

const RESOURCES = Resources.Components.Merchant.OrbitL;

type OrbitLDashboardModuleProps = {
  merchantPublicID: string;
};

export const OrbitLDashboardModule = ({
  merchantPublicID,
}: OrbitLDashboardModuleProps) => {
  const { t } = useResource();
  const {
    data: status,
    isLoading,
    isError,
  } = useGetOrbitLStatusQuery(merchantPublicID);

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (isError || !status) {
    return (
      <Layout>
        <Alert severity="error">{t(RESOURCES.Messages.LoadStatusError)}</Alert>
      </Layout>
    );
  }

  const {
    phase,
    checklist,
    isSelfEmployed,
    contractDownloadedAt,
    paymentServicesDownloadedAt,
    isLoanContractRequired,
    comgateStatus,
    merchantTerminalData,
    rejectionGroups,
  } = status;

  const rejections =
    comgateStatus === ComgateOnboardingStatus.ERRORS_IN_DOCUMENTS
      ? (rejectionGroups ?? [])
      : [];

  return (
    <Layout>
      <Stack gap={3}>
        {/* Status Display - always show */}
        <OrbitLStatusDisplay phase={phase} />

        {/* Phase: RequiresCompletion - show checklist */}
        {phase === OrbitLPhase.RequiresCompletion && (
          <OrbitLChecklist
            checklist={checklist}
            isSelfEmployed={isSelfEmployed}
          />
        )}

        {/* Phase: ReadyToDownloadContract - show checklist + contract download */}
        {phase === OrbitLPhase.ReadyToDownloadContract && (
          <>
            <OrbitLChecklist
              checklist={checklist}
              isSelfEmployed={isSelfEmployed}
            />
            <OrbitLContractConfirmation
              merchantPublicID={merchantPublicID}
              contractDownloadedAt={contractDownloadedAt}
              paymentServicesDownloadedAt={paymentServicesDownloadedAt}
              isLoanContractRequired={isLoanContractRequired}
            />
          </>
        )}

        {/* Phase: ContractDownloaded - show checklist if incomplete + contract confirmation */}
        {phase === OrbitLPhase.ContractDownloaded && (
          <>
            {!checklist.isComplete && (
              <OrbitLChecklist
                checklist={checklist}
                isSelfEmployed={isSelfEmployed}
              />
            )}
            <OrbitLContractConfirmation
              merchantPublicID={merchantPublicID}
              contractDownloadedAt={contractDownloadedAt}
              paymentServicesDownloadedAt={paymentServicesDownloadedAt}
              isLoanContractRequired={isLoanContractRequired}
            />
          </>
        )}

        {/* Phase: Submitted or Processing - show processing message or rejection */}
        {(phase === OrbitLPhase.SubmittedToComgate ||
          phase === OrbitLPhase.ProcessingByComgate) &&
          (rejections.length > 0 ? (
            <OrbitLRejectionPanel
              groups={rejections}
              isSelfEmployed={isSelfEmployed}
            />
          ) : (
            <Alert severity="info">{t(RESOURCES.Messages.Processing)}</Alert>
          ))}

        {/* Phase: TerminalDispatched - show handover confirmation */}
        {phase === OrbitLPhase.TerminalDispatched && (
          <OrbitLHandoverConfirmation
            merchantPublicID={merchantPublicID}
            merchantTerminalData={merchantTerminalData}
          />
        )}

        {/* Phase: Completed - show success message */}
        {phase === OrbitLPhase.Completed && (
          <Alert severity="success">
            {t(RESOURCES.Messages.CompletedSuccess)}
          </Alert>
        )}
      </Stack>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();
  return (
    <PrimaryPaper>
      <PaperTitle>{t(RESOURCES.Title)}</PaperTitle>
      <Box>{children}</Box>
    </PrimaryPaper>
  );
};
